import React, { useState, useRef, useEffect } from 'react';
import { Search, Sun, Moon, Bell, Plus, X } from 'lucide-react';
import { useTasks } from '../context/TaskContext';
import { formatDate } from './TaskCard';

const Header = ({ search, setSearch, onAddTask, pageTitle }) => {
  const currentUser = JSON.parse(localStorage.getItem("currentUser"));
  const { dark, setDark, alertTasks, settings } = useTasks();
  const [showNotif, setShowNotif] = useState(false);
  const notifRef = useRef(null);

  const tasks = JSON.parse(localStorage.getItem("tasks")) || [];

  const dueSoon = tasks.filter(task => {
    const today = new Date();
    const deadline = new Date(task.deadline);

    const diff = (deadline - today) / (1000 * 60 * 60 * 24);

    return diff <= 1 && diff >= 0;
  });

  {
    dueSoon.length > 0 && (
      <div>
        🔔 {dueSoon.length} tasks due soon
      </div>
    )
  }



  useEffect(() => {
    const handler = (e) => { if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotif(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <header className="flex items-center gap-3 px-5 py-3 border-b border-gray-800 dark:border-gray-800 bg-white dark:bg-[#161929] flex-shrink-0 z-10">
      <div className="flex flex-col">
        <h1 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          {pageTitle}
        </h1>

        {currentUser && (
          <span className="text-xs text-gray-400">
            Hello, {currentUser.name}👋
          </span>
        )}
      </div>

      {/* Search */}
      <div className="relative flex-1 max-w-sm">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Search tasks..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full pl-8 pr-3 py-2 text-sm bg-gray-100 dark:bg-[#1e2235] border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-gray-200 placeholder-gray-400 outline-none focus:border-indigo-500 transition-colors"
        />
        {search && (
          <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
            <X size={13} />
          </button>
        )}
      </div>

      <div className="flex items-center gap-2 ml-auto">
        {/* Notifications */}
        {settings.notifications && (
          <div className="relative" ref={notifRef}>
            <button
              onClick={() => setShowNotif(v => !v)}
              className="relative w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 dark:bg-[#1e2235] border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
            >
              <Bell size={15} />
              {alertTasks.length > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[9px] rounded-full flex items-center justify-center font-bold">
                  {alertTasks.length}
                </span>
              )}
            </button>

            {showNotif && (
              <div className="absolute right-0 top-10 w-72 bg-white dark:bg-[#1e2235] border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl z-50 animate-scale-in overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Alerts</span>
                  <span className="text-xs text-gray-400">{alertTasks.length} active</span>
                </div>
                {alertTasks.length === 0 ? (
                  <div className="px-4 py-6 text-center text-xs text-gray-400">No alerts right now 🎉</div>
                ) : (
                  <div className="max-h-64 overflow-y-auto scrollbar-thin">
                    {alertTasks.map(t => (
                      <div key={t.id} className="px-4 py-3 border-b border-gray-50 dark:border-gray-700/50 last:border-0 hover:bg-gray-50 dark:hover:bg-[#22263a] transition-colors">
                        <div className="flex items-start gap-2">
                          <span className="text-sm mt-0.5">{t.deadline && new Date(t.deadline) < new Date() ? '🔴' : '🟡'}</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-gray-800 dark:text-gray-200 truncate">{t.title}</p>
                            <p className="text-[11px] text-gray-400 mt-0.5">
                              {t.deadline && new Date(t.deadline) < new Date() ? 'Overdue · ' : 'Due soon · '}
                              {formatDate(t.deadline)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Theme toggle */}
        <div className="flex items-center bg-gray-100 dark:bg-[#1e2235] border border-gray-200 dark:border-gray-700 rounded-lg p-0.5 gap-0.5">
          <button
            onClick={() => setDark(true)}
            className={`w-7 h-7 flex items-center justify-center rounded-md transition-all ${dark ? 'bg-indigo-500 text-white' : 'text-gray-400 hover:text-gray-600'}`}
          >
            <Moon size={13} />
          </button>
          <button
            onClick={() => setDark(false)}
            className={`w-7 h-7 flex items-center justify-center rounded-md transition-all ${!dark ? 'bg-indigo-500 text-white' : 'text-gray-400 hover:text-gray-600'}`}
          >
            <Sun size={13} />
          </button>
        </div>

        {/* Add task */}

        <button
          onClick={onAddTask}
          className="flex items-center gap-1.5 px-3 py-2 bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-medium rounded-lg transition-all hover:-translate-y-px active:scale-95"
        >
          <Plus size={15} />
          <span className="hidden sm:inline">Add Task</span>
        </button>

        {/* Logout button */}
        <button
          onClick={() => {
            localStorage.removeItem("currentUser");
            window.location.href = "/";
          }}
          className="px-3 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-lg transition-all hover:-translate-y-px active:scale-95"
        >
          Logout
        </button>
      </div>
    </header>
  );
};

export default Header;
